import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { IndustryService } from '../../../../services/industry.service';
import { WhatsappShareService } from '../../../../services/whatsapp-share.service';

@Component({
  selector: 'app-sales-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    RadioButtonModule,
    ButtonModule,
    TooltipModule,
  ],
  templateUrl: './sales-history-dialog.component.html',
  styleUrl: './sales-history-dialog.component.scss',
})
export class SalesHistoryDialogComponent implements OnChanges, OnDestroy {
  @Input() transactions: any[] = [];
  @Input() flattenedTransactions: any[] = [];
  @Input() selectedTransactionIndex: number = -1;
  @Input() currentSearchTerm: string = '';
  @Input() showFooter: boolean = true;

  readonly isLoyaltyEnabled: boolean = inject(IndustryService).isLoyaltyEnabled();
  readonly whatsappShareService = inject(WhatsappShareService);
  sharingInvoiceNo: string | null = null;

  /** True between a refresh request and the next transactions list arriving (or a 10s safety timeout). */
  refreshing = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  @Output() selectedTransactionIndexChange = new EventEmitter<number>();
  @Output() transactionSelect = new EventEmitter<{ index: number; event?: Event }>();
  @Output() refreshRequested = new EventEmitter<void>();
  @Output() printReceiptRequested = new EventEmitter<string | any>();
  @Output() downloadPdfRequested = new EventEmitter<{ pdfUrl: string; invoiceNo: string; event?: Event }>();

  constructor(private messageService: MessageService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const listChanged =
      (changes['transactions'] && !changes['transactions'].firstChange) ||
      (changes['flattenedTransactions'] && !changes['flattenedTransactions'].firstChange);
    if (listChanged) {
      this.stopRefreshing();
    }
    // Pick up a Super Admin enable / disable each time the sales list is (re)loaded
    if (changes['transactions'] || changes['flattenedTransactions']) {
      this.whatsappShareService.loadApiStatus();
    }
  }

  ngOnDestroy(): void {
    this.stopRefreshing();
  }

  /** Called by whoever triggers a reload (this footer or the host dialog's footer) to show the loading state. */
  markRefreshing(): void {
    this.stopRefreshing();
    this.refreshing = true;
    this.refreshTimer = setTimeout(() => this.stopRefreshing(), 10000);
  }

  private stopRefreshing(): void {
    this.refreshing = false;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  onTransactionSelectByIndex(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedTransactionIndex = index;
    this.selectedTransactionIndexChange.emit(index);
    this.transactionSelect.emit({ index, event });
  }

  highlightSearchTerm(text: string): string {
    if (!this.currentSearchTerm || !text) return text;
    const searchTerm = this.currentSearchTerm;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  downloadInvoicePdf(pdfUrl: string, invoiceNo: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!pdfUrl) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No PDF Available',
        detail: 'Invoice PDF is not available for download',
        life: 3000,
      });
      return;
    }
    try {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `invoice_${invoiceNo}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.messageService.add({
        severity: 'success',
        summary: 'Download Started',
        detail: `Downloading invoice ${invoiceNo}...`,
        life: 2000,
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Download Failed',
        detail: 'Failed to download invoice PDF',
        life: 3000,
      });
    }
    // this.downloadPdfRequested.emit({ pdfUrl, invoiceNo, event });
  }

  async shareInvoiceWhatsapp(transactionItem: any, event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    if (this.sharingInvoiceNo) return;
    this.sharingInvoiceNo = transactionItem?.invoiceno;
    try {
      await this.whatsappShareService.shareTransaction(transactionItem, this.messageService, 'FO_SALES_HISTORY');
    } finally {
      this.sharingInvoiceNo = null;
    }
  }

  refreshHistoryData(): void {
    this.markRefreshing();
    this.refreshRequested.emit();
  }

  printReceipt(pdfUrl?: string | any): void {
    console.log(pdfUrl,'urlssss')
    this.printReceiptRequested.emit(pdfUrl);
  }
}
